import { useQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { CompanyApi } from "../../../../hooks/useCompanyApi"
import { translateMeetingAnalysis } from "../../../../lib/api"
import { i18n } from "../../../../lib/i18n"
import { queryKeys } from "../../../../lib/query-keys"

export interface IAvailableTranslation {
  locale: string
  stale: boolean
}

/**
 * Structural subset of the loaded meeting this hook reads. Kept local (rather
 * than importing the strict `IMeetingDetail`) so `analysis` stays the dynamic
 * JSON record the translation surface treats it as — the route passes the Eden
 * `fetchMeeting` result, whose `analysis` is looser than the hand-written type.
 */
export interface ITranslatableMeeting {
  analysis: unknown
  analysis_locale?: string
  analysis_version?: number
  available_translations?: { locale: string; stale: boolean }[]
}

export interface IUseMeetingTranslation {
  sourceLocale: string
  analysisLocale: string
  availableTranslations: IAvailableTranslation[]
  displayAnalysis: Record<string, unknown> | null
  isTranslating: boolean
  hasTranslationError: boolean
  onLocaleChange: (locale: string) => void
}

/**
 * Pick which analysis locale to show when the user hasn't chosen one yet.
 *
 * Prefer the UI language when that translation is already cached (no credit
 * burn). Otherwise stay on the meeting's source locale. Never treat a loading
 * placeholder of "en" as a user pick — that used to open Dutch meetings in
 * English, then keep English if an `en` translation existed.
 */
export function resolveInitialAnalysisLocale(args: {
  sourceLocale: string | undefined
  availableLocales: readonly string[]
  uiLocale: string
}): string {
  const source = (args.sourceLocale ?? "").toLowerCase()
  const ui = args.uiLocale.toLowerCase()
  const available = new Set(args.availableLocales.map((locale) => locale.toLowerCase()))
  if (ui && ui !== source && available.has(ui)) return ui
  if (source) return source
  if (ui) return ui
  return "en"
}

/**
 * Per-meeting language switcher state, extracted verbatim from the
 * meeting-detail route (Phase 4 of multi-language). Owns the picked-locale
 * state, the source-locale latch effect, the per-(locale, version) translation
 * query, and the resolved `displayAnalysis`. No coupling to the route's
 * pipeline / SSE / render-gate logic.
 */
export function useMeetingTranslation(
  company: CompanyApi | null,
  orgId: string | null,
  meetingId: string,
  meeting: ITranslatableMeeting | undefined,
): IUseMeetingTranslation {
  const sourceLocale = (meeting?.analysis_locale ?? "en").toLowerCase()
  const availableTranslations = useMemo(
    () =>
      (meeting?.available_translations ?? []).map((entry) => ({
        locale: entry.locale.toLowerCase(),
        stale: entry.stale,
      })),
    [meeting?.available_translations],
  )
  const [analysisLocale, setAnalysisLocale] = useState<string>("")
  const userPickedRef = useRef(false)
  const latchedMeetingId = useRef(meetingId)

  useEffect(() => {
    if (latchedMeetingId.current !== meetingId) {
      latchedMeetingId.current = meetingId
      userPickedRef.current = false
    }
    if (!meeting) {
      setAnalysisLocale("")
      return
    }
    if (userPickedRef.current) return
    setAnalysisLocale(
      resolveInitialAnalysisLocale({
        sourceLocale: meeting.analysis_locale,
        availableLocales: availableTranslations.map((entry) => entry.locale),
        uiLocale: i18n.locale,
      }),
    )
  }, [meeting, meetingId, availableTranslations])

  // Include analysis_version in the cache key so an edit (which bumps the
  // version server-side) invalidates the client's cached translation and
  // forces a refetch — that's the auto-refresh behaviour for stale entries.
  const analysisVersion = meeting?.analysis_version ?? 0
  const translationQuery = useQuery({
    queryKey: queryKeys.meetingTranslation(orgId, meetingId, analysisLocale, analysisVersion),
    queryFn: async () => {
      if (!company) throw new Error("company unavailable")
      return translateMeetingAnalysis(company, meetingId, analysisLocale)
    },
    // Only fire when the user picked a locale different from the source.
    enabled: !!company && !!meeting?.analysis && analysisLocale !== "" && analysisLocale !== sourceLocale,
    staleTime: Number.POSITIVE_INFINITY, // server caches per version; we cache per (locale, version)
    retry: 1,
  })

  // Resolved analysis to render: source if user is on source locale, else
  // the translated payload (or the source while the request is in flight).
  const displayAnalysis = useMemo<Record<string, unknown> | null>(() => {
    if (!meeting?.analysis) return null
    if (analysisLocale === sourceLocale) return meeting.analysis as Record<string, unknown>
    if (translationQuery.data) return translationQuery.data.analysis as Record<string, unknown>
    return meeting.analysis as Record<string, unknown>
  }, [meeting?.analysis, analysisLocale, sourceLocale, translationQuery.data])

  const onLocaleChange = useCallback((locale: string): void => {
    userPickedRef.current = true
    setAnalysisLocale(locale.toLowerCase())
  }, [])

  return {
    sourceLocale,
    analysisLocale,
    availableTranslations,
    displayAnalysis,
    isTranslating: translationQuery.isFetching,
    hasTranslationError: translationQuery.isError,
    onLocaleChange,
  }
}
